<template>
  <component
    :is="wrapperComp"
    v-bind="compProps"
    class="link"
    :class="[`gap-${gap}`, { anim: hasAnimateIcon, underline: hasHoverUnderline }]">
    <ImagesSvg
      v-if="icon"
      :icon="icon"
      :width="iconSize"
      :height="iconSize"
      :class="['link__icon', `link__icon--${iconColor}`]" />
    <div
      v-bind="cTextAttrs"
      :class="['link__text', `link__text--${textColor}`, { bold: hasFontBold }]">
      {{ cText }}
    </div>
  </component>
</template>

<script lang="ts" setup>
import type { Color } from '@base/types/color';
import type { Icon } from '@base/types/icon';
import type { FieldResponseModelOfString } from '@api';
import type { ConcreteComponent } from 'vue';

export interface IconTextLink {
  text: FieldResponseModelOfString | string;
  icon: Icon;
  link?: string;
  gap?: number;
  iconSize?: number;
  textColor?: Color;
  iconColor?: Color;
  hasFontBold?: boolean;
  hasHoverUnderline?: boolean;
  hasAnimateIcon?: boolean;
  target?: string;
}

const props = withDefaults(defineProps<IconTextLink>(), {
  gap: 2,
  iconSize: 16,
  link: '',
  textColor: 'white',
  iconColor: 'white',
  hasFontBold: true,
  hasHoverUnderline: true,
  hasAnimateIcon: true,
  target: '_self',
});

const cTextAttrs = computed(() => typeof props.text !== 'string' && useEpiAttrs(props.text));

const cText = computed(() => {
  return typeof props.text === 'string' ? props.text : props.text.value;
});

const wrapperComp = computed<ConcreteComponent | string>(() => {
  if (props.link && props.link.length) {
    return resolveComponent('NuxtLink');
  }

  return 'button';
});

const compProps = computed((): Record<string, string> => {
  if (wrapperComp.value === 'button') {
    return { type: 'button' };
  }

  return { to: props.link };
});
</script>

<style lang="scss" scoped>
$this: '.link';

#{$this} {
  display: flex;
  line-height: px(32);
  min-height: px(32);
  text-align: start;
  cursor: pointer;
  @include reset-btn;

  &__text {
    @include color-variants;
  }

  &__icon {
    align-self: flex-start;
    margin-block: px(8);
    flex-shrink: 0;
    transition: transform 0.2s;
    @include color-variants;
  }

  &.anim:hover {
    #{$this}__icon {
      transform: translateX(px(4));
    }
  }

  &.underline:hover {
    #{$this}__text {
      transition: color 0.2s;
      text-decoration: underline;
      @include color-variants-hover;
    }
  }
}
</style>
